import { useEffect, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import * as Accordion from '@radix-ui/react-accordion';
import { unfold } from '../utils/motion';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, fmtN, money, num, range, telHref, titleCase } from '../utils/quinn';
import { useIsEditing, useMediaQuery, useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';
import { feeFacts } from '../utils/fees';
import { Lightbox, useLightbox } from '../utils/lightbox';
import { useTourModal } from '../utils/details';
import { PLAN_OUTLINE, type Floorplan } from './FloorplansList';

interface Plan extends Floorplan {
  floorplan_price_max?: string;
}

interface Tour {
  virtual_tour_title?: string;
  virtual_tour_url?: string;
}

const homesCtaDef: SlotGroupDefinition = {
  label: 'See Available Homes Button',
  description: 'On every plan with homes ready. While the text is unchanged it carries the count ("See 5 Available Homes").',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Available Homes' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_online_leasing_url',
      suggestedFields: ['property_online_leasing_url', 'property_apply_now_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'On every plan with homes ready. Without a self-guided tour URL the default becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const askCtaDef: SlotGroupDefinition = {
  label: 'Ask About This Plan Button',
  description: 'On every plan with no homes available.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Ask About This Plan' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

type Sort = 'price-desc' | 'price-asc' | 'available' | 'size';
const SORTS: Array<[Sort, string, string]> = [
  ['price-desc', 'Price High to Low', 'High to Low'],
  ['price-asc', 'Price Low to High', 'Low to High'],
  ['available', 'Available First', 'Available First'],
  ['size', 'Largest', 'Largest'],
];
type View = 'cards' | 'table';
const CARDS_FIRST = 8;

const norm = (s: string | null | undefined) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const factsBase =
  'grid auto-cols-[minmax(0,190px)] grid-flow-col border-y border-y-[--q-border] [&>div+div]:border-l [&>div+div]:border-l-[--q-border] [&>div+div]:pl-5 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-col [&>div]:gap-1.5 [&>div]:py-4 [&_b]:font-heading [&_b]:text-[24px] [&_b]:font-normal [&_b]:leading-[30px] [&_b]:[overflow-wrap:break-word] max-[1299px]:[&>div+div]:pl-4 max-[1299px]:[&_b]:text-[20px] max-[1299px]:[&_b]:leading-[26px]';
/** The fact grid inside a phone accordion row: two columns, a rule under every cell. */
const factsRow =
  'max-[767px]:grid-flow-row max-[767px]:grid-cols-[1fr_1fr] max-[767px]:[border-bottom:0] max-[767px]:[&>div]:border-b max-[767px]:[&>div]:border-b-[--q-border] max-[767px]:[&>div]:py-3 max-[767px]:[&>div:nth-child(odd)]:pl-0 max-[767px]:[&>div:nth-child(odd)]:[border-left:0] max-[767px]:[&>div:nth-child(even)]:pl-4 max-[767px]:[&_.label]:text-[11px] max-[767px]:[&_.label]:leading-[14px] max-[767px]:[&_b]:text-[20px] max-[767px]:[&_b]:leading-6';
const viewBtn =
  'viewswitch__btn [&&]:h-9 [&&]:min-h-0 [&&]:px-4 [&&]:text-[--q-ink] aria-checked:[&&]:text-[--q-on-primary] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-width:1.3]';
const linkTarget = '[&.textlink]:inline-flex [&.textlink]:min-h-11 [&.textlink]:items-center';

const Chevron = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d={open ? 'M4 10L8 6L12 10' : 'M4 6L8 10L12 6'} />
  </svg>
);
const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M8.5 1.5H12.5V5.5M5.5 12.5H1.5V8.5M12.5 1.5L8 6M1.5 12.5L6 8" />
  </svg>
);
const CardsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1" /><rect x="8" y="1.5" width="4.5" height="4.5" rx="1" />
    <rect x="1.5" y="8" width="4.5" height="4.5" rx="1" /><rect x="8" y="8" width="4.5" height="4.5" rx="1" />
  </svg>
);
const TableIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M1.5 3h11M1.5 7h11M1.5 11h11" />
  </svg>
);

/**
 * FloorplansList for the floor plans page (Paper 04): Cards view (available plans as wide cards, the rest in a
 * "Not available right now" group) or Table view; on phones, one accordion of every plan.
 */
export function FloorPlansCompare() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const phone = useMediaQuery('(max-width: 767px)');
  const lb = useLightbox();
  const tourModal = useTourModal();
  const plans = useCollectionBinding<Plan>('floorplanscompare.plans', { collection: 'floorplans' });
  const homes = useSlotGroup('floorplanscompare.homes_cta', homesCtaDef);
  const tour = useSlotGroup('floorplanscompare.tour_cta', tourCtaDef);
  const ask = useSlotGroup('floorplanscompare.ask_cta', askCtaDef);

  const [beds, setBeds] = useState<number | 'all'>('all');
  const [availOnly, setAvailOnly] = useState(false);
  const [sort, setSort] = useState<Sort>('price-desc');
  const [view, setView] = useState<View>('cards');
  const [allCards, setAllCards] = useState(false);
  const [openKey, setOpenKey] = useState<string | null | undefined>(undefined);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('view') === 'table') setView('table');
    const onView = (e: Event) => setView((e as CustomEvent<View>).detail || 'table');
    window.addEventListener('quinn:plans-view', onView);
    return () => window.removeEventListener('quinn:plans-view', onView);
  }, []);

  const pickView = (v: View) => {
    setView(v);
    const url = new URL(location.href);
    if (v === 'table') url.searchParams.set('view', 'table');
    else url.searchParams.delete('view');
    history.replaceState(null, '', url.toString());
  };

  const tours = ((p.virtual_tours as Tour[]) || []).filter((t) => clean(t.virtual_tour_url));
  const fees = feeFacts(p);
  const charges = fees.monthlyRange ? `+ ${fees.monthlyRange}/mo required charges` : '';
  const garage = ((p.amenities as Array<{ title?: string }>) || []).map((a) => a.title || '').find((t) => /garage/i.test(t));
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const tel = telHref(flags.phone);
  const homesHref = homes.url || clean(p.property_online_leasing_url) || flags.applyUrl;

  const rows = plans
    .filter((f) => clean(f.floorplan_name))
    .map((f, i) => {
      const name = titleCase(f.floorplan_name);
      const vt = tours.find((t) => norm(t.virtual_tour_title) === norm(name)) || tours.find((t) => norm(t.virtual_tour_title).includes(norm(name)));
      const price = num(clean(f.floorplan_price_min));
      return {
        key: `${clean(f.floorplan_id) || 'plan'}-${i}`,
        name,
        img: clean(f.floorplan_image_url),
        beds: num(f.floorplan_beds_min),
        baths: num(f.floorplan_baths_min),
        sqft: num(f.floorplan_sqft_min),
        sqftMax: num(clean(f.floorplan_sqft_max)),
        price,
        priceMax: num(clean(f.floorplan_price_max)),
        avail: num(clean(f.floorplan_available_units)) || 0,
        tourUrl: vt ? clean(vt.virtual_tour_url) : null,
      };
    });
  type Row = (typeof rows)[number];

  const bedCounts = [...new Set(rows.map((r) => r.beds).filter((b): b is number => b != null))].sort((a, b) => a - b);
  const anyAvail = rows.some((r) => r.avail > 0);
  const byPrice = (a: Row, b: Row, dir: 1 | -1) => (a.price == null || b.price == null ? (a.price == null ? (b.price == null ? 0 : 1) : -1) : dir * (a.price - b.price));
  const shown = rows
    .filter((r) => (beds === 'all' || r.beds === beds) && (!availOnly || r.avail > 0))
    .sort((a, b) => {
      if (sort === 'price-desc') return byPrice(a, b, -1);
      if (sort === 'price-asc') return byPrice(a, b, 1);
      if (sort === 'available') return (b.avail > 0 ? 1 : 0) - (a.avail > 0 ? 1 : 0) || byPrice(a, b, 1);
      return (b.sqft ?? 0) - (a.sqft ?? 0);
    });
  const ready = shown.filter((r) => r.avail > 0);
  const waiting = shown.filter((r) => r.avail === 0);
  const shownReady = allCards || editing ? ready : ready.slice(0, CARDS_FIRST);
  // Phones: until the visitor opens or closes one, the first plan with homes ready starts open.
  const open = openKey === undefined ? (shown.find((r) => r.avail > 0) || shown[0])?.key ?? null : openKey;

  if (!rows.length && !editing) return null;

  const facts = (r: Row, sep = ' · ', long = false) =>
    [r.beds != null && `${r.beds} ${long ? 'bed' : 'bd'}`, r.baths != null && `${r.baths} ${long ? 'bath' : 'ba'}`, r.sqft && `${fmtN(r.sqft)} sq ft`].filter(Boolean).join(sep);
  const rent = (r: Row) => (r.price ? (r.priceMax && r.priceMax > r.price ? `${money(r.price)} – ${money(r.priceMax)}` : money(r.price)!) : 'Call for pricing');
  const sameBedsAvailable = (b: number | null) => b != null && rows.some((x) => x.beds === b && x.avail > 0);
  const showSameBeds = (b: number) => {
    setBeds(b);
    setAvailOnly(true);
    setExpanded(null);
    setOpenKey(undefined);
    document.getElementById('compare-plans')?.scrollIntoView({ block: 'start' });
  };
  const openPlan = (r: Row) => r.img && lb.open(Math.max(0, rows.filter((x) => x.img).findIndex((x) => x.key === r.key)));
  const lbItems = rows.filter((x) => x.img).map((x) => ({ src: x.img!, alt: `${x.name} floor plan`, caption: x.name, desc: facts(x, ' · ', true) }));

  /** The section's slot groups are edited from the first card that shows them; the rest repeat its values. */
  const wrapHomes = (first: boolean, node: ReactNode) =>
    first ? <EditableSlotGroup groupPath="floorplanscompare.homes_cta" definition={homesCtaDef}>{node}</EditableSlotGroup> : node;
  const wrapTour = (first: boolean, node: ReactNode) =>
    first ? <EditableSlotGroup groupPath="floorplanscompare.tour_cta" definition={tourCtaDef}>{node}</EditableSlotGroup> : node;
  const wrapAsk = (first: boolean, node: ReactNode) =>
    first ? <EditableSlotGroup groupPath="floorplanscompare.ask_cta" definition={askCtaDef}>{node}</EditableSlotGroup> : node;

  const homesLabel = (r: Row) => (!homes.label || homes.label === 'See Available Homes' ? `See ${r.avail} Available ${r.avail === 1 ? 'Home' : 'Homes'}` : homes.label);
  const homesBtn = (r: Row, first: boolean, extra = '') =>
    homesHref &&
    wrapHomes(
      first,
      <a className={`btn btn-primary ${extra}`} href={homesHref} target={homes._target || '_self'} data-vibesite-event={homes.vibesite_event || undefined}>
        {homesLabel(r)} <span className="btn__arrow">→</span>
      </a>,
    );
  const tourBtn = (first: boolean, cls: string) =>
    wrapTour(
      first,
      <a className={cls} href={tourLink.href} target={tourLink.isTour ? tour._target || '_self' : '_self'} data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}>
        {tourLink.label}
      </a>,
    );
  const askBtn = (first: boolean, cls: string) =>
    wrapAsk(
      first,
      <a className={cls} href={ask.url || PATHS.contact} target={ask._target || '_self'}>
        {ask.label || 'Ask About This Plan'}
      </a>,
    );
  const tour3d = (r: Row, label: string) =>
    r.tourUrl && (
      <a className="textlink" href={r.tourUrl} target="_blank" rel="noopener" data-vibesite-event="virtual_tour_clicked" onClick={tourModal.open(r.tourUrl, `${r.name} · 3D tour`)}>
        {label} <span className="btn__arrow">→</span>
      </a>
    );
  const factCells = (r: Row, row = false) => (
    <div className={row ? `${factsBase} ${factsRow}` : factsBase}>
      <div><span className="label">Bedrooms</span><b>{r.beds ?? '—'}</b></div>
      <div><span className="label">Bathrooms</span><b>{r.baths ?? '—'}</b></div>
      <div><span className="label">Sq ft</span><b>{r.sqft ? range(r.sqft, r.sqftMax) : '—'}</b></div>
      {garage && <div><span className="label">Garage</span><b>{/attached/i.test(garage) ? 'Attached' : 'Included'}</b></div>}
    </div>
  );

  /** A wide plan card: the drawing on white with name, 3D and Expand on it, then status, price, facts and actions. */
  const card = (r: Row, k: number) => {
    const isReady = r.avail > 0;
    return (
      <article
        key={r.key}
        className={`flex overflow-hidden rounded-lg border border-[--q-border] bg-[--q-paper] [@media(max-width:991px)]:flex-col${isReady ? '' : ' fpcard2--waiting'}`}
      >
        <div
          className={`relative h-[360px] flex-[0_0_min(540px,48%)] border-r border-r-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-contain [&>svg]:inset-[60px] [&>svg]:h-[calc(100%-120px)] [&>svg]:w-[calc(100%-120px)] max-[1199px]:h-auto max-[1199px]:min-h-[320px] max-[1199px]:basis-[44%] [@media(max-width:991px)]:h-[320px] [@media(max-width:991px)]:basis-auto [@media(max-width:991px)]:border-b [@media(max-width:991px)]:border-b-[--q-border] [@media(max-width:991px)]:[border-right:0]`}
        >
          {r.img ? <VibesitesImage url={r.img} alt={`${r.name} floor plan`} sizes="540px" /> : PLAN_OUTLINE}
          <span className="pill pill--primary absolute left-5 top-5">{r.name}</span>
          {r.tourUrl && (
            <a
              className="pill absolute right-5 top-5 border border-[--q-border] bg-[--q-paper] text-[--q-ink] no-underline hover:border-[--q-primary-on-paper]"
              href={r.tourUrl}
              target="_blank"
              rel="noopener"
              data-vibesite-event="virtual_tour_clicked"
              onClick={tourModal.open(r.tourUrl, `${r.name} · 3D tour`)}
            >
              3D tour
            </a>
          )}
          {r.img && (
            <button
              className="absolute bottom-4 right-4 inline-flex h-11 items-center gap-2 rounded-full border border-[--q-muted] bg-[--q-paper] px-4 text-sm font-medium text-[--q-ink] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:1.4] hover:border-[--q-ink]"
              type="button"
              onClick={() => openPlan(r)}
            >
              <ExpandIcon /> Expand
            </button>
          )}
        </div>
        <div className="flex min-w-0 flex-auto flex-col justify-between gap-6 px-10 pb-8 pt-9 max-[1199px]:p-7">
          <div className="flex items-start justify-between gap-6">
            <div className="flex min-w-0 flex-col gap-1.5">
              <h3 className="m-0 font-heading text-[40px] font-light leading-[48px] tracking-[--q-tracking-tight] max-[1199px]:text-[32px] max-[1199px]:leading-10">
                {r.name}
              </h3>
              <span
                className={`whitespace-pre-wrap text-sm font-medium leading-5 ${isReady ? 'text-[--vibe-color-success]' : 'text-[--q-muted]'}`}
              >
                ●&nbsp;&nbsp;{isReady ? `${r.avail} ${r.avail === 1 ? 'home' : 'homes'} available now` : 'No homes available right now'}
              </span>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 text-right">
              {r.price ? (
                <>
                  <div className="flex items-baseline gap-1.5 text-sm leading-5 text-[--q-muted]">
                    <span>from</span>
                    <b className="font-heading text-[32px] font-normal leading-[38px] text-[--q-secondary]">{money(r.price)}</b>
                    <span>/mo</span>
                  </div>
                  <div className="text-[13px] leading-[18px] text-[--q-muted]">{rent(r)} by home</div>
                  {charges && <div className="text-[13px] font-medium leading-[18px]">{charges}</div>}
                </>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 text-sm leading-5 text-[--q-muted]">
                    <b className="font-heading text-[24px] font-normal leading-[30px] text-[--q-ink]">Call for pricing</b>
                  </div>
                  {flags.phone && <div className="dniphone text-[13px] leading-[18px] text-[--q-muted]">{flags.phone}</div>}
                </>
              )}
            </div>
          </div>
          {factCells(r)}
          <div className="flex flex-wrap items-center justify-between gap-4 [&>.textlink]:inline-flex [&>.textlink]:min-h-11 [&>.textlink]:items-center">
            <div className="flex flex-wrap gap-3 [&_.btn]:min-h-12 [&_.btn]:px-[22px] [&_.btn]:text-[15px]">
              {isReady ? (
                <>
                  {homesBtn(r, k === 0)}
                  {tourBtn(k === 0, 'btn btn-secondary')}
                </>
              ) : (
                <>
                  {askBtn(false, 'btn btn-primary')}
                  {tel && <a className="btn btn-secondary dniphonehref" href={tel}>Call Leasing</a>}
                </>
              )}
            </div>
            {isReady
              ? tour3d(r, 'Take the 3D Tour')
              : sameBedsAvailable(r.beds) && (
                  <button className="textlink" type="button" onClick={() => showSameBeds(r.beds!)}>
                    See Available {r.beds}-Bedrooms <span className="btn__arrow">→</span>
                  </button>
                )}
          </div>
        </div>
      </article>
    );
  };

  const bar = (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-y border-y-[--q-border] py-4 max-[767px]:flex-col max-[767px]:items-stretch max-[767px]:gap-3">
      {bedCounts.length > 1 && (
        <div
          className="flex flex-wrap items-center gap-2 [&_.pillbtn:not([aria-pressed=true]):hover]:border-[--q-muted] [&_.pillbtn]:min-h-11 [&_.pillbtn]:border-[--q-muted] [&_.pillbtn]:px-[18px] max-[767px]:mx-[calc(-1*var(--gutter))] max-[767px]:flex-nowrap max-[767px]:overflow-x-auto max-[767px]:px-[--gutter] max-[767px]:[scrollbar-width:none] max-[767px]:[&::-webkit-scrollbar]:hidden"
          role="group"
          aria-label="Filter by bedrooms"
        >
          <span className="label mr-1">Bedrooms</span>
          <button className="pillbtn" type="button" aria-pressed={beds === 'all'} onClick={() => setBeds('all')}>All Beds</button>
          {bedCounts.map((b) => (
            <button key={b} className="pillbtn" type="button" aria-pressed={beds === b} onClick={() => setBeds(b)}>
              {b} {b === 1 ? 'Bedroom' : 'Bedrooms'}
            </button>
          ))}
        </div>
      )}
      <div className="ml-auto flex min-w-0 items-center gap-7 max-[767px]:ml-0 max-[767px]:justify-between max-[767px]:gap-3">
        {phone && anyAvail && (
          <button
            className="group/switch inline-flex min-h-11 min-w-0 items-center gap-2.5 text-left text-sm font-medium text-[--q-ink]"
            type="button"
            role="switch"
            aria-checked={availOnly}
            onClick={() => setAvailOnly(!availOnly)}
          >
            <span
              className="inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-[--q-muted] p-0.5 [transition:background-color_var(--q-motion-fast)] group-aria-checked/switch:bg-[--q-primary]"
              aria-hidden="true"
            >
              <span className="h-4 w-4 rounded-full bg-[--q-paper] [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] group-aria-checked/switch:bg-[--q-on-primary] group-aria-checked/switch:[transform:translateX(16px)] motion-reduce:[transition:none]" />
            </span>
            <RichTextContent path="floorplanscompare.available_label" as="span" className="[&_p]:m-0" />
          </button>
        )}
        <label className="relative inline-flex h-11 min-w-0 items-center gap-2.5 rounded-md border border-[--q-muted] bg-[--q-paper] px-3.5 text-sm focus-within:[outline:2px_solid_var(--q-primary-on-paper)] focus-within:outline-offset-2 max-[767px]:shrink-[2]">
          <span className="text-[--q-muted]">Sort</span>
          <select
            className="min-w-0 flex-auto cursor-pointer appearance-none text-ellipsis bg-transparent py-0 pl-0 pr-[18px] text-[--q-ink] [-webkit-appearance:none] [border:0] [font:inherit] [&&]:font-medium focus-visible:[outline:none]"
            value={sort}
            onChange={(e) => setSort(e.target.value as Sort)}
          >
            {SORTS.map(([v, l, s]) => (
              <option key={v} value={v}>{phone ? s : l}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 text-[--q-ink]" aria-hidden="true">▾</span>
        </label>
        {!phone && rows.length > 1 && (
          <div className="viewswitch [&.viewswitch]:h-11 [&.viewswitch]:border-[--q-muted]" role="radiogroup" aria-label="View">
            <button className={viewBtn} type="button" role="radio" aria-checked={view === 'cards'} onClick={() => pickView('cards')}>
              <CardsIcon /> Cards
            </button>
            <button className={viewBtn} type="button" role="radio" aria-checked={view === 'table'} onClick={() => pickView('table')}>
              <TableIcon /> Table
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const table = (
    <div className="[@media(max-width:991px)]:overflow-x-auto" hidden={phone || view !== 'table'}>
      <table className="w-full border-collapse text-[15px] leading-[22px] [&_td:first-child]:pl-0 [&_td]:border-b [&_td]:border-b-[--q-border] [&_td]:px-3 [&_td]:py-4 [&_td]:text-left [&_td]:align-middle [&_th:first-child]:pl-0 [&_th]:border-b [&_th]:border-b-[--q-border] [&_th]:px-3 [&_th]:py-4 [&_th]:text-left [&_th]:align-middle [&_tbody_th]:font-normal [&_thead_th]:border-b-[--q-ink] [&_thead_th]:pb-3 [&_thead_th]:pt-0 [&_thead_th]:text-[12px] [&_thead_th]:font-medium [&_thead_th]:uppercase [&_thead_th]:leading-4 [&_thead_th]:tracking-[--q-tracking-label] [&_thead_th]:text-[--q-muted]">
        <caption className="sr-only">Floor plans, prices and availability</caption>
        <thead>
          <tr>
            <th scope="col">Plan</th>
            <th scope="col">Beds</th>
            <th scope="col">Baths</th>
            <th scope="col">Sq ft</th>
            <th scope="col">Rent</th>
            <th scope="col">Available now</th>
            <th scope="col">3D tour</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.key}>
              <th scope="row">
                <span className="flex items-center gap-4 font-heading text-[22px] leading-7">
                  <span className="relative h-12 w-[72px] shrink-0 overflow-hidden rounded-sm border border-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-[3px] [&_img]:h-[calc(100%-6px)] [&_img]:w-[calc(100%-6px)] [&_img]:object-contain">
                    {r.img ? <VibesitesImage url={r.img} alt="" sizes="72px" /> : PLAN_OUTLINE}
                  </span>
                  {r.name}
                </span>
              </th>
              <td>{r.beds ?? '—'}</td>
              <td>{r.baths ?? '—'}</td>
              <td>{r.sqft ? range(r.sqft, r.sqftMax) : '—'}</td>
              <td>{r.price ? `${rent(r)}/mo` : 'Call for pricing'}</td>
              <td className={r.avail > 0 ? 'font-medium text-[--vibe-color-success]' : ''}>{r.avail > 0 ? `● ${r.avail} ${r.avail === 1 ? 'home' : 'homes'}` : 'None right now'}</td>
              <td>{tour3d(r, 'Take the 3D Tour') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mx-0 mb-0 mt-4 text-[13px] leading-[18px] text-[--q-muted]">
        {bedCounts.length ? `Every plan here has ${bedCounts.length > 1 ? `${bedCounts.slice(0, -1).join(', ')} or ${bedCounts[bedCounts.length - 1]}` : bedCounts[0]} bedroom${bedCounts.length === 1 && bedCounts[0] === 1 ? '' : 's'}. ` : ''}
        Pricing and availability update daily.
      </p>
    </div>
  );

  /** Phones: every plan in one accordion (Paper 04 mobile FloorplansList). */
  const accordion = (
    <Accordion.Root type="single" collapsible value={open ?? ''} onValueChange={(v) => setOpenKey(v || null)} className="flex flex-col">
      {shown.map((r, k) => {
        const isOpen = open === r.key;
        const isReady = r.avail > 0;
        return (
          <Accordion.Item key={r.key} value={r.key} asChild>
            <article className="border-b border-b-[--q-border]">
              <Accordion.Header asChild>
                <Accordion.Trigger className="group/fprow flex w-full items-center gap-4 py-[18px] text-left text-inherit max-[767px]:gap-3 max-[767px]:py-3.5">
                  <span className="relative h-[84px] w-28 shrink-0 overflow-hidden rounded-md border border-[--q-border] bg-[--q-plan-surface] [&>svg]:inset-2.5 [&>svg]:h-[calc(100%-20px)] [&>svg]:w-[calc(100%-20px)] [&_img]:absolute [&_img]:inset-1 [&_img]:h-[calc(100%-8px)] [&_img]:w-[calc(100%-8px)] [&_img]:object-contain max-[767px]:h-16 max-[767px]:w-[84px]">
                    {r.img ? <VibesitesImage url={r.img} alt="" sizes="84px" /> : PLAN_OUTLINE}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5 [flex:1_1_0]">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="font-heading text-[24px] leading-[30px] group-hover/fprow:text-[--q-primary-on-paper] max-[767px]:text-[22px] max-[767px]:leading-7">{r.name}</span>
                      {r.price ? (
                        <span className="whitespace-nowrap font-heading text-[20px] leading-[26px] text-[--q-secondary] max-[767px]:text-[18px] max-[767px]:leading-6">{money(r.price)}+</span>
                      ) : (
                        <span className="whitespace-nowrap text-[13px] font-medium leading-[18px]">Call for pricing</span>
                      )}
                    </span>
                    <span className="text-[13px] leading-[18px] text-[--q-muted]">{facts(r)}</span>
                    <span className={`text-[13px] font-medium leading-[18px] ${isReady ? 'text-[--vibe-color-success]' : 'text-[--q-muted]'}`}>
                      ● {isReady ? `${r.avail} available` : 'None available'}
                    </span>
                  </span>
                  <span
                    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[1.5px] [transition:background-color_var(--q-motion-fast),border-color_var(--q-motion-fast)] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:2] ${
                      isOpen ? 'border-[--q-primary] bg-[--q-primary] text-[--q-on-primary]' : 'border-[--q-ink] bg-[--q-paper] text-[--q-ink]'
                    }`}
                  >
                    <Chevron open={isOpen} />
                  </span>
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className={unfold}>
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 pb-8 pl-32 pt-1 max-[1199px]:pl-0 max-[767px]:grid-cols-[1fr] max-[767px]:gap-3.5 max-[767px]:pb-[18px] max-[767px]:pt-0">
                  <div className="relative h-80 overflow-hidden rounded-md border border-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-4 [&_img]:h-[calc(100%-32px)] [&_img]:w-[calc(100%-32px)] [&_img]:object-contain max-[767px]:h-[180px] max-[767px]:[&_img]:inset-2 max-[767px]:[&_img]:h-[calc(100%-16px)] max-[767px]:[&_img]:w-[calc(100%-16px)]">
                    {r.img ? <VibesitesImage url={r.img} alt={`${r.name} floor plan`} sizes="100vw" /> : PLAN_OUTLINE}
                  </div>
                  {isReady ? (
                    <>
                      <div className="flex flex-col gap-0.5 text-[13px] leading-[18px]">
                        {r.price && <div className="text-[--q-muted]">{rent(r)} by home</div>}
                        {charges && <div className="font-medium">{charges}</div>}
                      </div>
                      {homesBtn(r, k === shown.findIndex((x) => x.avail > 0), '[&.btn]:w-full')}
                      <div className="flex justify-between gap-4 [&_.textlink]:inline-flex [&_.textlink]:min-h-11 [&_.textlink]:items-center">
                        {tourBtn(false, 'textlink')}
                        {tour3d(r, '3D Tour')}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="max-[767px]:text-[13px] max-[767px]:font-medium max-[767px]:leading-[18px] max-[767px]:text-[--q-muted]">● No homes available right now</div>
                      {flags.phone && <div className="text-sm leading-5">Call for pricing · <span className="dniphone">{flags.phone}</span></div>}
                      {factCells(r, true)}
                      <div className="flex gap-2 [&>*]:[flex:1_1_0] [&_.btn]:w-full max-[767px]:flex-col max-[767px]:[&_.btn]:min-h-11 max-[767px]:[&_.btn]:text-sm">
                        {askBtn(k === shown.findIndex((x) => x.avail === 0), 'btn btn-primary')}
                        {tel && <a className="btn btn-secondary dniphonehref" href={tel}>Call Leasing</a>}
                      </div>
                      {sameBedsAvailable(r.beds) && (
                        <button className={`textlink self-center ${linkTarget}`} type="button" onClick={() => showSameBeds(r.beds!)}>
                          See Available {r.beds}-Bedrooms <span className="btn__arrow">→</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </Accordion.Content>
            </article>
          </Accordion.Item>
        );
      })}
    </Accordion.Root>
  );

  const waitingBeds = waiting.map((r) => r.beds).find((b) => sameBedsAvailable(b));

  return (
    <section
      className="section [&.section]:pb-4xl [&.section]:pt-6 max-[767px]:[&.section]:pb-12 max-[767px]:[&.section]:pt-0"
      id="compare-plans"
    >
      <h2 className="sr-only">Compare floor plans</h2>
      {bar}
      <EditableCollectionBinding bindingPath="floorplanscompare.plans" label="Floor Plans" className="mt-6 flex flex-col gap-6">
        {phone ? (
          accordion
        ) : (
          <div className="flex flex-col gap-5" hidden={view !== 'cards'}>
            {shownReady.map((r, k) => card(r, k))}
            {ready.length > shownReady.length && (
              <div className="flex justify-center">
                <button className="btn btn-secondary" type="button" onClick={() => setAllCards(true)}>
                  Show {ready.length - shownReady.length} more {ready.length - shownReady.length === 1 ? 'plan' : 'plans'}
                </button>
              </div>
            )}
            {waiting.length > 0 && (
              <div className="flex flex-col pt-6">
                <div className="flex items-baseline justify-between gap-6 border-b border-b-[--q-ink] pb-4">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <RichTextContent
                      path="floorplanscompare.unavailable_title"
                      as="h3"
                      className="m-0 font-heading text-[24px] font-light leading-8 [&_p]:m-0"
                    />
                    <RichTextContent path="floorplanscompare.unavailable_note" as="p" className="m-0 text-sm leading-5 text-[--q-muted] [&_p]:m-0" />
                  </div>
                  {waitingBeds != null && (
                    <button className="textlink" type="button" onClick={() => showSameBeds(waitingBeds)}>
                      See Available {waitingBeds}-Bedrooms ↑
                    </button>
                  )}
                </div>
                {waiting.map((r, k) =>
                  expanded === r.key ? (
                    <div key={r.key} className="flex flex-col gap-2 border-b border-b-[--q-border] py-4">
                      {card(r, -1)}
                      <button className={`textlink self-end ${linkTarget}`} type="button" onClick={() => setExpanded(null)}>Collapse Plan ↑</button>
                    </div>
                  ) : (
                    <div
                      key={r.key}
                      className="flex items-center gap-8 border-b border-b-[--q-border] py-4 max-[1199px]:flex-wrap max-[1199px]:gap-x-6 max-[1199px]:gap-y-4"
                    >
                      <span className="relative h-[110px] w-[176px] shrink-0 overflow-hidden rounded-md border border-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-1.5 [&_img]:h-[calc(100%-12px)] [&_img]:w-[calc(100%-12px)] [&_img]:object-contain">
                        {r.img ? <VibesitesImage url={r.img} alt="" sizes="176px" /> : PLAN_OUTLINE}
                      </span>
                      <span className="flex flex-[0_0_300px] flex-col gap-1 max-[1199px]:basis-auto">
                        <span className="font-heading text-[28px] font-light leading-[34px]">{r.name}</span>
                        <span className="text-sm leading-5 text-[--q-muted]">{facts(r, ' · ', true)}</span>
                      </span>
                      <span className="flex flex-auto flex-col gap-1 text-sm leading-5">
                        <span className="font-medium text-[--q-muted]">● No homes available right now</span>
                        <span>{r.price ? `from ${money(r.price)}/mo` : 'Call for pricing'}{flags.phone ? <> · <span className="dniphone">{flags.phone}</span></> : null}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-4 [&_.textlink]:inline-flex [&_.textlink]:min-h-11 [&_.textlink]:items-center">
                        <button className="textlink" type="button" onClick={() => setExpanded(r.key)}>Expand Plan</button>
                        {askBtn(k === 0, 'btn btn-secondary btn--sm')}
                      </span>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
        {table}
      </EditableCollectionBinding>

      {!shown.length && rows.length > 0 && (
        <div className="flex flex-col items-start gap-2 py-8 text-[--q-muted] [&_p]:m-0">
          <RichTextContent path="floorplanscompare.empty" as="p" />
          <button
            className="textlink"
            type="button"
            onClick={() => {
              setBeds('all');
              setAvailOnly(false);
            }}
          >
            Show every plan
          </button>
        </div>
      )}
      <Lightbox items={lbItems} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} />
      {tourModal.node}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansCompare',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanscompare.available_label': { label: 'Availability Switch Text (phone)', type: 'richtext', defaultValue: '<p>Available Now Only</p>' },
    'floorplanscompare.unavailable_title': { label: 'Not Available Heading', type: 'richtext', defaultValue: '<p>Not available right now</p>' },
    'floorplanscompare.unavailable_note': {
      label: 'Not Available Note',
      type: 'richtext',
      defaultValue: '<p>No open homes in these plans today. Ask the leasing team about upcoming availability.</p>',
    },
    'floorplanscompare.empty': { label: 'No Matching Plans Text', type: 'richtext', defaultValue: '<p>No plans match these filters right now.</p>' },
  },
  slotGroups: {
    'floorplanscompare.homes_cta': homesCtaDef,
    'floorplanscompare.tour_cta': tourCtaDef,
    'floorplanscompare.ask_cta': askCtaDef,
  },
  collectionBindings: {
    'floorplanscompare.plans': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorPlansCompare as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansCompare;
